import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAiAssist, useAiBarOpen } from '@/lib/use-ai-assist';
import { AiAssistBar } from '@/components/AiAssistBar';
import {
  useContactSuggestions,
  useGmailInboxes,
  useMyTeamMember,
  useReplyTemplates,
  useTeamMembers,
  useTicket,
  useTicketMessages,
  type ReplyTemplate,
} from '@/lib/queries';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BookmarkPlus,
  FileText,
  Paperclip,
  Pencil,
  Send,
  Sparkles,
  SlidersHorizontal,
  Wand2,
  X,
  Zap,
} from 'lucide-react';
import { ShortcutTooltip } from '@/components/ShortcutTooltip';
import { cn } from '@/lib/utils';
import { useKeyboardShortcuts } from '@/lib/use-keyboard-shortcuts';
import { consumeAssistFollowup } from '@/lib/assist-followup';
import { SaveAsTemplateDialog } from '@/components/SaveAsTemplateDialog';
import { EmailChipsInput } from '@/components/EmailChipsInput';
import { AttachmentsRow } from '@/components/AttachmentsRow';
import {
  RichBodyEditor,
  consumePendingImages,
  findInlineImageIds,
  registerPendingImages,
  type RichBodyEditorHandle,
} from '@/components/RichBodyEditor';
import { htmlToPlain } from '@/lib/html-normalize';
import { firstNameFrom, plainToHtmlParagraphs, renderTemplateForInsert } from '@/lib/template-render';
import { enqueueReply, takeJob } from '@/lib/outbox-jobs';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useDraftCollision } from '@/lib/use-draft-collision';
import { useAccess } from '@/lib/use-access';
import {
  afterSendPermission,
  describeAfterSend,
  parseAfterSend,
  type AfterSend,
} from '@/lib/macros';
import { collisionSummary } from '@/lib/draft-collision';
import { onRestoreRequest } from '@/lib/outbox-restore';
import type { ReplyOutboxPayload } from '@/lib/outbound-send';
import { useOutboxJobs } from '@/lib/use-send-outbox';
import { RichBodyToolbar } from '@/components/RichBodyToolbar';
import { KbSuggestions } from '@/components/KbSuggestions';
import { ToneCheckStrip } from '@/components/ToneCheckStrip';
import { useComposerAttachments } from '@/lib/use-composer-attachments';
import {
  buildOwnAddresses,
  dedupeAddresses,
  deriveReplyAllRecipients,
} from '@/lib/reply-recipients';

type Mode = 'reply' | 'reply_all' | 'forward';

// localStorage autosave: per-ticket key + a TTL so abandoned drafts don't
// pile up forever. The team almost never wants a 2-week-old draft when they
// reopen a ticket, so we expire on read.
const DRAFT_KEY_PREFIX = 'pa-draft:';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface SavedDraft {
  savedAt: number;
  body: string;
  mode: Mode;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  /** The template's "then …" action, if one is attached (see lib/macros.ts). */
  afterSend?: AfterSend | null;
}

function draftKey(ticketId: string): string {
  return DRAFT_KEY_PREFIX + ticketId;
}

function loadDraft(ticketId: string): SavedDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(ticketId));
    if (!raw) return null;
    const d = JSON.parse(raw) as SavedDraft;
    if (typeof d?.savedAt !== 'number' || Date.now() - d.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(draftKey(ticketId));
      return null;
    }
    // Recipients-only entries aren't real drafts — they get auto-populated
    // by the mode-change effect on every mount and would otherwise fire
    // the "Draft restored" toast on every ticket visit. Treat any entry
    // with an empty body as not-a-draft and clean up legacy entries from
    // before this guard existed.
    if (!d.body || d.body.trim().length === 0) {
      localStorage.removeItem(draftKey(ticketId));
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

function saveDraft(ticketId: string, d: Omit<SavedDraft, 'savedAt'>) {
  try {
    // A draft only counts when the user has actually written something.
    // Recipients alone are not worth persisting — the mode-change effect
    // can re-derive them deterministically from the ticket. This stops
    // every ticket-detail mount from auto-creating a "draft" the next
    // visit would announce as restored.
    if (d.body.trim().length === 0) {
      localStorage.removeItem(draftKey(ticketId));
      return;
    }
    localStorage.setItem(
      draftKey(ticketId),
      JSON.stringify({ ...d, savedAt: Date.now() } as SavedDraft),
    );
  } catch {
    // localStorage can throw (private mode, quota). Silent — autosave is
    // best-effort.
  }
}

function clearDraft(ticketId: string) {
  try {
    localStorage.removeItem(draftKey(ticketId));
  } catch {
    /* ignore */
  }
}


export function ReplyComposer({
  ticketId,
  onSent,
  onDraftingChange,
  othersDrafting = [],
}: {
  ticketId: string;
  onSent?: () => void;
  /** Tells the page when there is text in the reply box (shown to teammates as "writing"). */
  onDraftingChange?: (drafting: boolean) => void;
  /** Teammates who have text in their own reply box on this ticket right now. */
  othersDrafting?: string[];
}) {
  const qc = useQueryClient();
  // body is now an HTML string (the editor's innerHTML). Plain-text
  // derivations live in `htmlToPlain(body)`.
  const [body, setBody] = useState('');
  const editorRef = useRef<RichBodyEditorHandle>(null);

  // Is there a reply in the box? Drives the collision baseline and the "writing" presence.
  const hasDraft = htmlToPlain(body).trim().length > 0 || /<img\b/i.test(body);
  useEffect(() => {
    onDraftingChange?.(hasDraft);
  }, [hasDraft, onDraftingChange]);
  useEffect(() => () => onDraftingChange?.(false), [onDraftingChange]);
  // The client wrote again / a teammate replied since the draft began.
  const collision = useDraftCollision(ticketId, hasDraft);
  const [collisionAsk, setCollisionAsk] = useState(false);

  const ticketQ = useTicket(ticketId);
  const messagesQ = useTicketMessages(ticketId);
  const teamMembersQ = useTeamMembers();
  const templatesQ = useReplyTemplates();
  const templates = templatesQ.data ?? [];
  // Surface a tiny "Signature: <name>" / "no signature set" hint so the
  // sender knows whose footer is about to land in the recipient's inbox.
  const myTmQ = useMyTeamMember();
  const hasOwnSignature =
    !!(myTmQ.data?.signature_html && myTmQ.data.signature_html.trim().length > 0)
    || !!(myTmQ.data?.signature_text && myTmQ.data.signature_text.trim().length > 0);

  // A template with a macro ("…then resolve") attaches it here when inserted. It rides along
  // with the reply and runs only after the reply has been sent (lib/after-send.ts).
  const [afterSend, setAfterSend] = useState<AfterSend | null>(null);
  const access = useAccess();
  const memberName = (slackId: string) =>
    teamMembersQ.data?.find((m) => m.slack_user_id === slackId)?.name || undefined;

  const [mode, setMode] = useState<Mode>('reply');
  // True once the person clicks a mode pill, so the auto "open in Reply all"
  // default below never overrides a deliberate choice.
  const modePickedRef = useRef(false);
  const pickMode = (m: Mode) => {
    modePickedRef.current = true;
    setMode(m);
  };
  // Save-as-template dialog open state. Local to the composer; the
  // dialog itself reads the live `body` (HTML) at submit time so
  // there's no need to snapshot it here.
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  // Toolbar-driven template picker. Mirrors the `/` slash menu but
  // anchored to a button so an agent who already typed (and thus can't
  // start a fresh `/`) can still insert a template anywhere in their
  // current text. Insertion happens at the live caret position and
  // doesn't delete any prefix.
  const [insertTemplateOpen, setInsertTemplateOpen] = useState(false);
  const [insertTemplateQuery, setInsertTemplateQuery] = useState('');
  const insertTemplatePopoverRef = useRef<HTMLDivElement | null>(null);
  // Close the toolbar template picker when the user clicks anywhere
  // outside its anchor + popover. Listens at the document level so
  // clicks on the editor, other toolbar buttons, or empty page area
  // all dismiss it. The button's own onClick toggles, so we check
  // for that element via a data-attribute to avoid a re-open/re-close
  // race when the user clicks the button to dismiss.
  useEffect(() => {
    if (!insertTemplateOpen) return;
    function onDocClick(ev: MouseEvent) {
      const popover = insertTemplatePopoverRef.current;
      if (!popover) return;
      const target = ev.target as Node | null;
      if (!target) return;
      // Click inside the popover → ignore (item-click handles dismissal).
      if (popover.contains(target)) return;
      // Click on the toolbar trigger → its own onClick toggles; bail
      // so we don't fight it.
      if (target instanceof Element && target.closest('[data-insert-template-trigger]')) {
        return;
      }
      setInsertTemplateOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [insertTemplateOpen]);
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);

  // Contacts autocomplete — one query string + suggestion list per field, so
  // typing in Cc doesn't clobber a suggestion already showing in To.
  const [toQuery, setToQuery] = useState('');
  const [ccQuery, setCcQuery] = useState('');
  const [bccQuery, setBccQuery] = useState('');
  const toSuggestions = useContactSuggestions(toQuery, to);
  const ccSuggestions = useContactSuggestions(ccQuery, cc);
  const bccSuggestions = useContactSuggestions(bccQuery, bcc);
  const [subject, setSubject] = useState<string>(''); // Forward only

  // Attachments — paperclip + paste + drag/drop. Uploaded on Send.
  const attachments = useComposerAttachments();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Draft autosave bookkeeping.
  // - skipNextAutoFillRef: when we restore a saved draft on mount, the
  //   mode-change effect would otherwise fire once (state goes from initial
  //   'reply' to whatever was restored) and overwrite our To/Cc/Bcc. We set
  //   this ref during restore and consume it in the auto-fill effect so the
  //   first run is a no-op. Subsequent (real) mode changes work normally.
  // - draftRestoredOnceRef: signal that we've shown the "Restored from
  //   earlier draft" toast for this mount, so it doesn't repeat on rerenders.
  // - draftWasRestored: flag for showing the "Discard draft" affordance in
  //   the footer (visible only when we actually loaded one from storage).
  const skipNextAutoFillRef = useRef(false);
  // Set when the mount effect restored a draft, so the "open in Reply all"
  // default can tell "restored" apart from "nothing saved" without re-reading
  // localStorage (the autosave effect can clear it mid-mount).
  const draftRestoredRef = useRef(false);
  const draftRestoredOnceRef = useRef(false);
  const [draftWasRestored, setDraftWasRestored] = useState(false);

  // Default mode populates To with just the ticket's sender. Reply All adds
  // the latest inbound message's recipients minus us. Forward clears To.
  // We re-run this whenever mode changes OR the underlying ticket/messages
  // become available.
  const ticket = ticketQ.data;
  const messages = messagesQ.data ?? [];
  const latestMessage = messages[messages.length - 1];

  // Who Reply All addresses: the latest client author in To, everyone else on
  // the client's side of the thread in Cc (see lib/reply-recipients.ts).
  const inboxesQ = useGmailInboxes();
  const ownAddresses = useMemo(() => buildOwnAddresses(inboxesQ.data), [inboxesQ.data]);
  const replyAll = useMemo(
    () =>
      deriveReplyAllRecipients({
        messages,
        ticketSenderEmail: ticket?.sender_email,
        ownAddresses,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messagesQ.data, ticket?.sender_email, ownAddresses],
  );
  // Primitive key so the auto-fill effect re-runs only when the recipients
  // actually change, not on every ticket/messages refetch (which used to wipe
  // addresses typed by hand after a claim/assign).
  const replyAllKey = replyAll.to.join(',') + '|' + replyAll.cc.join(',');
  // Open in Reply all when the client's side of the thread includes anyone
  // besides the sender; plain Reply otherwise.
  const defaultMode: Mode = replyAll.cc.length > 0 ? 'reply_all' : 'reply';
  // Back to a clean recipient set. setMode alone is a no-op when the mode is
  // unchanged, which would leave hand-edited chips behind after a send.
  const resetRecipients = (m: Mode) => {
    setMode(m);
    if (m === 'reply_all') {
      setTo(replyAll.to);
      setCc(replyAll.cc);
    } else {
      setTo(dedupeAddresses([ticket?.sender_email], ownAddresses));
      setCc([]);
    }
    setBcc([]);
  };

  // ── Mount: try to restore a saved draft for this ticket ─────────────────
  // Runs once per ticketId. If a draft is found, restores all fields and
  // sets `skipNextAutoFillRef` so the auto-fill effect below doesn't
  // overwrite the restored To/Cc/Bcc on its first synthetic run.
  useEffect(() => {
    const d = loadDraft(ticketId);
    if (d) {
      // Body is stored as HTML now. For drafts written before the rich-
      // editor refactor, the saved body might be plain text; detect via
      // tag presence and wrap in <p> if needed.
      const restoredHtml = /<\w+[^>]*>/.test(d.body) ? d.body : plainToHtmlParagraphs(d.body);
      setBody(restoredHtml);
      // The editor mounts AFTER this effect (the JSX is below); push
      // the hydration into a microtask via rAF so the editor ref exists.
      requestAnimationFrame(() => editorRef.current?.setHtml(restoredHtml));
      setMode(d.mode);
      setTo(d.to);
      setCc(d.cc);
      setBcc(d.bcc);
      setSubject(d.subject);
      setAfterSend(parseAfterSend(d.afterSend?.action, d.afterSend?.payload));
      if (d.cc.length > 0) setShowCc(true);
      if (d.bcc.length > 0) setShowBcc(true);
      skipNextAutoFillRef.current = true;
      draftRestoredRef.current = true;
      setDraftWasRestored(true);
      if (!draftRestoredOnceRef.current) {
        draftRestoredOnceRef.current = true;
        toast.info('Draft restored from your last visit.', { duration: 3500 });
      }
      return;
    }

    // No saved draft — check for a one-shot Customer Assist follow-up
    // prefill staged by the session-ended widget card. Single-use
    // (consume clears the sessionStorage key) so navigating away +
    // back doesn't re-apply it. Loses to a real saved draft if both
    // exist, so an in-flight edit isn't clobbered.
    const followup = consumeAssistFollowup(ticketId);
    if (followup) {
      setBody(followup);
      requestAnimationFrame(() => editorRef.current?.setHtml(followup));
      // Mode stays 'reply'; the auto-fill effect populates To from the
      // ticket's sender. We do NOT set skipNextAutoFillRef here — we
      // want the regular Reply derivation to run.
      toast.info('Live assist follow-up draft ready — finish the recap and send.', {
        duration: 4500,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // ── Mount: drop the cursor straight into the reply editor ───────────────
  // Now that the composer scrolls with the page (no longer sticky), we
  // want opening a ticket to land the user in "ready to type" state
  // without making them scroll down to find the composer. Calling
  // `focusEnd()` on the editor focuses it AND triggers the browser's
  // default focus-scroll, bringing the composer into view at the
  // bottom of the viewport. The thread above is still reachable by
  // scrolling up.
  //
  // Skipped on small screens — auto-popping the soft keyboard on
  // mobile/tablet is jarring and people often want to read the thread
  // first. Skipped if the user has already focused something else
  // (e.g. clicked into the internal-notes field before this fired).
  // Tracked per-ticket via a ref so switching tabs (Thread / Summary
  // / KB) doesn't re-focus and yank the page back down.
  const autoFocusedTicketIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ticket || !messagesQ.data) return;
    if (autoFocusedTicketIdRef.current === ticketId) return;
    autoFocusedTicketIdRef.current = ticketId;
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    const active = document.activeElement as HTMLElement | null;
    if (
      active &&
      active !== document.body &&
      (active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.isContentEditable)
    ) {
      return;
    }
    // Two rAFs + a tiny timeout: gives the draft-restore + mode-change
    // effects (both of which call setHtml via rAF) time to settle so
    // focus lands on the final content, not on a transient empty
    // state. 120ms is imperceptible but enough to win the race.
    const t = window.setTimeout(() => {
      editorRef.current?.focusEnd();
    }, 120);
    return () => window.clearTimeout(t);
  }, [ticketId, ticket, messagesQ.data]);

  // ── Mode change: auto-fill recipients + subject + quoted body ───────────
  useEffect(() => {
    if (!ticket) return;
    // First synthetic run after a draft restore — skip exactly once so the
    // restored values survive.
    if (skipNextAutoFillRef.current) {
      skipNextAutoFillRef.current = false;
      return;
    }
    if (mode === 'reply') {
      setTo(dedupeAddresses([ticket.sender_email], ownAddresses));
      setCc([]);
      setBcc([]);
      setSubject('');
    } else if (mode === 'reply_all') {
      setTo(replyAll.to);
      setCc(replyAll.cc);
      setBcc([]);
      if (replyAll.cc.length > 0) setShowCc(true);
      setSubject('');
    } else if (mode === 'forward') {
      // Forward: blank To, prefill Subject with "Fwd: ...".
      setTo([]);
      setCc([]);
      setBcc([]);
      const subj = ticket.subject ?? '(no subject)';
      setSubject(/^fwd?:/i.test(subj) ? subj : `Fwd: ${subj}`);
    }
    // `ticket` is read by field, deliberately — see replyAllKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, ticket?.id, ticket?.sender_email, ticket?.subject, replyAllKey]);

  // ── First load: open in the default mode ────────────────────────────────
  // Once per ticket, after messages arrive. Skipped when a draft exists (the
  // mount effect restored its mode) or the person already picked a mode.
  const defaultModeAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!ticket || !messagesQ.data || !inboxesQ.data) return;
    if (defaultModeAppliedRef.current === ticketId) return;
    defaultModeAppliedRef.current = ticketId;
    if (modePickedRef.current || draftRestoredRef.current || loadDraft(ticketId)) return;
    if (defaultMode !== 'reply') setMode(defaultMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, ticket, messagesQ.data, inboxesQ.data]);

  // ── Body prefill (Forward only) ──────────────────────────────────────────
  // Forward gets a "Forwarded message" header + quoted body — that's the
  // standard mail UX and the team needs it to add a brief note above the
  // forwarded content.
  //
  // Reply / Reply All deliberately do NOT prefill the body. The thread above
  // the composer already shows the original message; quoting in the composer
  // just clutters what the team has to scroll past while writing.
  //
  // Gated on `body.trim().length === 0` so user typing / a restored draft is
  // never clobbered.
  useEffect(() => {
    if (mode !== 'forward') return;
    if (htmlToPlain(body).trim().length > 0) return;
    if (!latestMessage) return;
    const date = new Date(latestMessage.created_at).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
    });
    const author = latestMessage.from_email ?? 'unknown';
    const quoted = (latestMessage.body ?? '')
      .split('\n')
      .map((l) => '> ' + l)
      .join('\n');
    const plain = [
      '',
      '',
      '---------- Forwarded message ----------',
      `From: ${author}`,
      `Date: ${date}`,
      `Subject: ${ticket?.subject ?? '(no subject)'}`,
      '',
      quoted,
    ].join('\n');
    const html = plainToHtmlParagraphs(plain);
    editorRef.current?.setHtml(html);
    setBody(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, latestMessage, ticket?.subject]);

  // ── Persist draft on every meaningful change ────────────────────────────
  // Synchronous write — strings are tiny, localStorage is fast, no debounce
  // needed for this volume. The "is empty" check inside saveDraft prevents
  // pollution when the composer first mounts with empty state.
  useEffect(() => {
    saveDraft(ticketId, { body, mode, to, cc, bcc, subject, afterSend });
  }, [ticketId, body, mode, to, cc, bcc, subject, afterSend]);

  // AI Draft / Polish. One hook owns the mutations, the images + links
  // round-trip, and a real undo stack (the editor's native Cmd+Z cannot undo a
  // programmatic setHtml, so the old "Cmd+Z if you preferred your wording" toast
  // was not true).
  const assist = useAiAssist({
    ticketId,
    getBodyHtml: () => body,
    applyHtml: (html) => {
      editorRef.current?.setHtml(html);
      setBody(html);
    },
  });
  const [aiBarOpen, toggleAiBar] = useAiBarOpen();

  // ── Send ─────────────────────────────────────────────────────────────────
  // Send does not send: it snapshots the message, hands it to the app-wide
  // outbox (lib/send-outbox.ts) and clears the composer, so the person is free
  // at once — they can start the next reply or leave the page. The outbox owns
  // the 5 s undo window, the uploads + send, the retry and the toasts, so none
  // of it depends on this component still being mounted.
  //
  // Why it left the component: `App.tsx` re-mounts the routed subtree on every
  // navigation. While this component owned the send, clicking Send and then
  // leaving the page cut the undo window short and sent immediately, and a
  // reply that failed after you had left had nowhere to say so.
  //
  // The idempotency key is minted per click and lives in the snapshot, so every
  // retry reuses it: if the first attempt did reach Gmail, workflow 12 answers
  // `deduped` instead of sending twice. (Three copies of one reply once reached
  // a client when each click minted a fresh key and the composer still looked
  // unsent. The composer now clears on click, so there is nothing to re-click.)
  const send = (confirmed = false) => {
    if (body.trim().length === 0) {
      toast.error('Type a reply before sending.');
      return;
    }
    if (to.length === 0) {
      toast.error('At least one To recipient is required.');
      return;
    }
    if (mode === 'forward' && !subject.trim()) {
      toast.error('Forward needs a subject.');
      return;
    }
    // Something new landed on the ticket while this was being written: ask first.
    if (!confirmed && collision.collisions.length > 0) {
      setCollisionAsk(true);
      return;
    }

    // Snapshot everything the send needs, right now. Pasted images move out of
    // the editor's registry into the snapshot (Undo puts them back).
    const payload: ReplyOutboxPayload = {
      ticketId,
      ticketSubject: ticket?.subject ?? null,
      idempotency_key: crypto.randomUUID(),
      mode,
      to,
      cc,
      bcc,
      subject: mode === 'forward' ? subject : undefined,
      snapshot: {
        bucket: ticketId,
        bodyHtml: body,
        files: attachments.items.map((it) => ({ localId: it.localId, file: it.file })),
        inlineImages: consumePendingImages(findInlineImageIds(body)),
        uploaded: new Map(),
      },
      afterSend: afterSend ?? undefined,
      actorSlackId: myTmQ.data?.slack_user_id ?? null,
    };
    enqueueReply(qc, payload);

    // A clean composer for the next reply. This used to happen only after the
    // server answered — the wait this change removes.
    setBody('');
    editorRef.current?.setHtml('');
    resetRecipients(defaultMode);
    setShowCc(false);
    setShowBcc(false);
    attachments.clear();
    setAfterSend(null);
    clearDraft(ticketId);
    setDraftWasRestored(false);
    // The ticket page may now open the next ticket ("Send & next"). The reply is in
    // the app-wide outbox, so leaving this page is safe, and Undo still works.
    onSent?.();
  };

  // ── Taking a queued reply back (Undo / Edit) ────────────────────────────
  // Put a reply the outbox is holding back into the editor: body, recipients,
  // mode and files, exactly as they were when Send was clicked.
  const restoreFrom = (p: ReplyOutboxPayload) => {
    // The mode-change effect would overwrite the restored recipients. Skip it
    // once — but only when the mode really changes, otherwise the effect never
    // runs and the flag would swallow a later legitimate run.
    if (p.mode !== mode) {
      skipNextAutoFillRef.current = true;
      setMode(p.mode);
    }
    modePickedRef.current = true;
    setTo(p.to);
    setCc(p.cc);
    setBcc(p.bcc);
    setSubject(p.subject ?? '');
    setShowCc(p.cc.length > 0);
    setShowBcc(p.bcc.length > 0);
    setBody(p.snapshot.bodyHtml);
    setAfterSend(p.afterSend ?? null);
    editorRef.current?.setHtml(p.snapshot.bodyHtml);
    registerPendingImages(p.snapshot.inlineImages);
    if (p.snapshot.files.length > 0) attachments.add(p.snapshot.files.map((f) => f.file));
  };
  const restoreFromRef = useRef(restoreFrom);
  restoreFromRef.current = restoreFrom;

  // ── Slash-menu state (templates) ───────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState('');
  const [menuActiveIdx, setMenuActiveIdx] = useState(0);
  const [slashStart, setSlashStart] = useState<number | null>(null);

  const matches = useMemo(() => {
    if (!menuOpen) return [] as ReplyTemplate[];
    const q = menuQuery.trim().toLowerCase();
    if (q.length === 0) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [menuOpen, menuQuery, templates]);

  useEffect(() => {
    if (menuOpen && matches.length === 0) {
      setMenuOpen(false);
      setSlashStart(null);
    }
  }, [menuOpen, matches.length]);

  useEffect(() => {
    if (menuActiveIdx >= matches.length && matches.length > 0) {
      setMenuActiveIdx(matches.length - 1);
    }
  }, [matches.length, menuActiveIdx]);

  // Compute the caret offset within the editor's plain-text content
  // (innerText). Used by the slash-menu logic so we can find templates by
  // looking back N chars from the caret in the same way the textarea
  // version did.
  function getEditorCaretOffset(): number | null {
    const el = editorRef.current?.getElement();
    if (!el) return null;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer)) return null;
    // Build a range from the start of the editor to the caret, then
    // measure its text length. This is the plain-text caret offset.
    const before = document.createRange();
    before.setStart(el, 0);
    before.setEnd(range.startContainer, range.startOffset);
    return before.toString().length;
  }

  function detectSlash(text: string, caret: number): number | null {
    for (let i = caret - 1; i >= 0; i--) {
      const ch = text[i];
      if (ch === '/') {
        const prev = i === 0 ? '\n' : text[i - 1];
        if (prev === '\n') return i;
        return null;
      }
      if (ch === '\n' || ch === ' ') return null;
    }
    return null;
  }

  // Editor onChange — fires with the rich-HTML innerHTML. We just store
  // it; slash-menu detection runs on the plain-text equivalent (so the
  // logic doesn't have to know about HTML structure).
  // Toolbar commands fire execCommand which mutates the editor's HTML
  // synchronously, but the editor's `onChange` only fires from user input
  // events. Pull the current HTML and run it through the same handler so
  // autosave + slash-menu state stay consistent after a B/I/U click.
  function syncBodyFromEditor() {
    const editor = editorRef.current;
    if (!editor) return;
    handleChange(editor.getHtml());
  }

  function handleChange(html: string) {
    setBody(html);
    const editor = editorRef.current;
    if (!editor) return;
    const text = editor.getText();
    const caret = getEditorCaretOffset() ?? text.length;
    const slashAt = detectSlash(text, caret);
    if (slashAt === null) {
      if (menuOpen) setMenuOpen(false);
      setSlashStart(null);
      return;
    }
    setSlashStart(slashAt);
    setMenuQuery(text.slice(slashAt + 1, caret));
    setMenuOpen(true);
    setMenuActiveIdx(0);
  }

  /** Turn a template body (plain text from older rows, or a rich-HTML payload
   *  saved from the composer) into insertion HTML, with `{{...}}` variables
   *  filled from this ticket.
   *
   *  The tag-detection heuristic and the escaping rule now live in
   *  `lib/template-render.ts` because ComposeDialog needs exactly the same
   *  behaviour and had drifted — it was still escaping HTML templates into
   *  visible markup. Keeping one implementation is what stops that recurring. */
  function renderTemplate(tpl: ReplyTemplate): string {
    return renderTemplateForInsert(tpl.body, {
      firstName: firstNameFrom(ticket?.sender_name, ticket?.sender_email),
      ticketSubject: ticket?.subject ?? '',
      senderEmail: ticket?.sender_email ?? '',
      ticketIdShort: ticketId.slice(0, 8),
    });
  }

  /** Insert a template at the current caret position. Shared by the
   *  slash menu (after deleting the `/<query>` prefix) and the new
   *  toolbar Templates button (which inserts in place, no prefix).
   *  Caller is responsible for any pre-deletion. */
  function insertTemplateAtCaret(tpl: ReplyTemplate) {
    const editor = editorRef.current;
    const editorEl = editor?.getElement();
    if (!editor || !editorEl) return;
    editorEl.focus();
    document.execCommand('insertHTML', false, renderTemplate(tpl));
    setBody(editor.getHtml());
    adoptMacro(tpl);
  }

  /** A template that also does something after sending ("…then resolve") attaches it as a chip
   *  next to Send. Skipped, with a note, when this person's role couldn't do that action here:
   *  the reply text is still inserted. A template without a macro leaves any chip as it is. */
  function adoptMacro(tpl: ReplyTemplate) {
    const macro = parseAfterSend(tpl.after_send_action, tpl.after_send_payload);
    if (!macro) return;
    const need = afterSendPermission(macro);
    if (need && !access.canIn(ticket?.inbox_id ?? null, need)) {
      toast.info(`This template also does "${describeAfterSend(macro, memberName)}" after sending, which your role can't do here. Only the text was inserted.`);
      return;
    }
    setAfterSend(macro);
  }

  // Replace the `/<query>` text BEFORE the caret with the template body.
  // For contentEditable: figure out the caret's current plain-text offset,
  // delete `caret - slashStart` chars before it (which removes `/<query>`),
  // then insert the rendered template HTML at the caret.
  function selectTemplate(tpl: ReplyTemplate) {
    if (slashStart === null) return;
    const editor = editorRef.current;
    const editorEl = editor?.getElement();
    if (!editor || !editorEl) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!editorEl.contains(range.startContainer)) return;

    // How many chars between slashStart and current caret, in plain text?
    const text = editor.getText();
    const caretOffset = getEditorCaretOffset() ?? text.length;
    const charsToDelete = Math.max(0, caretOffset - slashStart);

    // Build a deletion range: from the caret, walk backwards N text
    // characters, and select that span. document.execCommand('delete')
    // then wipes it; insertHTML inserts the template body.
    editorEl.focus();
    for (let i = 0; i < charsToDelete; i++) {
      sel.modify('extend', 'backward', 'character');
    }
    document.execCommand('delete');
    document.execCommand('insertHTML', false, renderTemplate(tpl));

    setMenuOpen(false);
    setSlashStart(null);
    setBody(editor.getHtml());
    adoptMacro(tpl);
  }

  function onEditorKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (menuOpen && matches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMenuActiveIdx((i) => Math.min(matches.length - 1, i + 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMenuActiveIdx((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectTemplate(matches[menuActiveIdx]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMenuOpen(false);
        setSlashStart(null);
        return;
      }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  }

  const drafting = assist.drafting;
  const polishing = assist.polishing;
  // Sending no longer holds the composer: a queued reply lives in the outbox.
  const anyBusy = drafting || polishing;
  // Empty if the rich body has no plain text AND no inline images.
  const isEmpty = htmlToPlain(body).trim().length === 0 && !/<img\b/i.test(body);

  // ── Hand a pending reply back to this composer ──────────────────────────
  // Two ways in: the person presses Undo while this ticket is open (restore at
  // once, if the box is empty), or presses Edit on the "Not sent" bubble /
  // failure toast (see lib/outbox-restore.ts).
  const composerEmptyRef = useRef(true);
  composerEmptyRef.current = isEmpty && attachments.items.length === 0;

  useEffect(
    () =>
      onRestoreRequest(ticketId, (job) => {
        if (
          !composerEmptyRef.current &&
          !window.confirm('Replace what you have typed here with this reply?')
        ) {
          return false;
        }
        const taken = takeJob<ReplyOutboxPayload>(job.id);
        if (!taken) return false;
        restoreFromRef.current(taken.payload);
        return true;
      }),
    [ticketId],
  );

  // Undo while the ticket is open: the message returns to the editor. Jobs
  // already on hold when this composer mounted stay as "Not sent" bubbles —
  // restoring them unasked could clobber what was typed since.
  const outboxJobs = useOutboxJobs(ticketId);
  const heldSeenRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const held = outboxJobs.filter((j) => j.kind === 'reply' && j.state === 'held');
    if (heldSeenRef.current === null) {
      heldSeenRef.current = new Set(held.map((j) => j.id));
      return;
    }
    for (const job of held) {
      if (heldSeenRef.current.has(job.id)) continue;
      heldSeenRef.current.add(job.id);
      if (!composerEmptyRef.current) continue; // stays as a bubble with Edit
      const taken = takeJob<ReplyOutboxPayload>(job.id);
      if (taken) restoreFromRef.current(taken.payload);
    }
  }, [outboxJobs]);

  // Composer keyboard shortcuts.
  //
  //   Cmd/Ctrl + Shift + D → AI draft from scratch (matches the Draft
  //     toolbar button; same enablement rules — disabled while any
  //     mutation is in flight).
  //   Cmd/Ctrl + Shift + P → Polish the current reply (matches the
  //     Polish toolbar button; needs non-empty body).
  //
  // Shift is required on both to dodge browser bindings (⌘D bookmarks,
  // ⌘P prints). allowInInput=true because the rich editor is
  // contenteditable — without it the shortcut would only fire when
  // the user clicked somewhere outside the editor first, defeating
  // the purpose.
  // With Shift held, browsers report `e.key` as the uppercase form on
  // US layouts — so match against 'D' / 'P', not 'd' / 'p'. Same
  // convention the global '?' shortcut uses (App.tsx).
  useKeyboardShortcuts([
    {
      key: 'D',
      ctrl: true,
      shift: true,
      allowInInput: true,
      handler: (e) => {
        if (anyBusy) return;
        e.preventDefault();
        assist.draft();
      },
    },
    {
      key: 'P',
      ctrl: true,
      shift: true,
      allowInInput: true,
      handler: (e) => {
        if (anyBusy || isEmpty) return;
        e.preventDefault();
        assist.polish();
      },
    },
  ]);

  return (
    <div className="relative">
      <div
        className={cn(
          'group/composer relative flex flex-col rounded-xl border border-border bg-background shadow-sm transition-shadow',
          'focus-within:border-foreground/40 focus-within:ring-1 focus-within:ring-foreground/15 focus-within:shadow-md',
        )}
      >
        {/* Mode pills + recipient block */}
        <div className="flex flex-col gap-1.5 border-b border-border/60 px-2.5 py-2">
          <div className="flex flex-wrap items-center gap-1">
            <ModePill active={mode === 'reply'} onClick={() => pickMode('reply')}>
              Reply
            </ModePill>
            <ModePill active={mode === 'reply_all'} onClick={() => pickMode('reply_all')}>
              Reply all
            </ModePill>
            <ModePill active={mode === 'forward'} onClick={() => pickMode('forward')}>
              Forward
            </ModePill>
            <span className="ml-2 text-3xs tabular-nums text-muted-foreground">
              {to.length + cc.length + bcc.length} recipient{to.length + cc.length + bcc.length === 1 ? '' : 's'}
            </span>
          </div>

          <EmailChipsInput
            label="To"
            values={to}
            onChange={setTo}
            placeholder={mode === 'forward' ? 'forward to…' : 'name@example.com'}
            onDraftChange={setToQuery}
            suggestions={toSuggestions}
            rightAction={
              <div className="ml-auto flex items-center gap-2 pr-1 text-3xs text-muted-foreground">
                {!showCc ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowCc(true); }}
                    className="cursor-pointer hover:text-foreground hover:underline"
                  >
                    Add Cc
                  </button>
                ) : null}
                {!showBcc ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowBcc(true); }}
                    className="cursor-pointer hover:text-foreground hover:underline"
                  >
                    Add Bcc
                  </button>
                ) : null}
              </div>
            }
          />

          {showCc || cc.length > 0 ? (
            <EmailChipsInput
              label="Cc"
              values={cc}
              onChange={setCc}
              onDraftChange={setCcQuery}
              suggestions={ccSuggestions}
              rightAction={
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowCc(false); setCc([]); }}
                  className="ml-auto pr-1 text-3xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  Remove
                </button>
              }
            />
          ) : null}
          {showBcc || bcc.length > 0 ? (
            <EmailChipsInput
              label="Bcc"
              values={bcc}
              onChange={setBcc}
              onDraftChange={setBccQuery}
              suggestions={bccSuggestions}
              rightAction={
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowBcc(false); setBcc([]); }}
                  className="ml-auto pr-1 text-3xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  Remove
                </button>
              }
            />
          ) : null}

          {mode === 'forward' ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5 transition-colors focus-within:border-foreground/40 focus-within:ring-1 focus-within:ring-foreground/15">
              <span className="select-none text-2xs font-medium uppercase tracking-wide text-muted-foreground">
                Subject
              </span>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject…"
                className="flex-1 border-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
              />
            </div>
          ) : null}
        </div>

        {/* Body — wrapped in a div that owns the drag/drop handlers so files
            dropped anywhere on the textarea attach themselves. Pasted /
            dragged IMAGES render as large preview cards above the textarea
            (Gmail-style — visible inside the message area, not buried as
            chips); non-image attachments stay below as small chips. */}
        <div {...attachments.dragHandlers}>
          {/* KB suggestions — only renders if workflow 01's triage left
              suggested_kb_article_ids on the ticket and we have those
              articles in the KB. Click → injects the article body at the
              caret. The user can edit before sending. */}
          {/* Someone got there first: the client wrote again, or a teammate replied,
              since you started this reply. Send asks before going out. */}
          {collision.collisions.length > 0 ? (
            <div
              role="alert"
              className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-tint-warning-ring bg-tint-warning px-3 py-2 text-xs text-tint-warning-fg"
            >
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 font-medium">
                {collisionSummary(
                  collision.collisions,
                  new Map((teamMembersQ.data ?? []).map((m) => [m.slack_user_id, m.name])),
                  ticket?.sender_name ?? '',
                )}{' '}
                <span className="font-normal opacity-90">What you are writing may be out of date.</span>
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-2xs"
                  onClick={() => {
                    const last = collision.collisions[collision.collisions.length - 1];
                    const el = last ? document.querySelector('[data-message-id="' + last.id.replace(/^msg:/, '') + '"]') : null;
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  Show
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-6 px-2 text-2xs" onClick={collision.acknowledge}>
                  Got it
                </Button>
              </span>
            </div>
          ) : null}
          <ConfirmDialog
            open={collisionAsk}
            onOpenChange={setCollisionAsk}
            title="Send anyway?"
            description={
              collisionSummary(
                collision.collisions,
                new Map((teamMembersQ.data ?? []).map((m) => [m.slack_user_id, m.name])),
                ticket?.sender_name ?? '',
              ) + ' Review the new message first, or send what you wrote.'
            }
            confirmLabel="Send anyway"
            cancelLabel="Review first"
            onConfirm={() => {
              setCollisionAsk(false);
              collision.acknowledge();
              send(true);
            }}
          />
          {othersDrafting.length > 0 ? (
            <p className="mb-2 inline-flex items-center gap-1.5 text-2xs text-muted-foreground">
              <Pencil className="h-3 w-3 shrink-0" aria-hidden />
              {othersDrafting.join(' and ')} {othersDrafting.length === 1 ? 'is' : 'are'} also writing a reply to this ticket
            </p>
          ) : null}
          <KbSuggestions ticketId={ticketId} editorRef={editorRef} />
          {/* Tone-check strip — Grammarly-style advisory. Renders only
              when the body is long enough AND Claude flags an actual
              concern. Never blocks Send; the user can ship anything. */}
          <ToneCheckStrip bodyText={htmlToPlain(body)} />
          {/* Rich body — contentEditable. Pasting an image inserts it
              inline at the caret (Gmail-style). Non-image files bubble up
              to the attachments cluster via onPasteFiles. */}
          <RichBodyEditor
            ref={editorRef}
            initialHtml={body}
            onChange={handleChange}
            onPasteFiles={(files) => attachments.add(files)}
            onKeyDown={onEditorKeyDown}
            placeholder={
              mode === 'forward'
                ? 'Add a header note above the forwarded message…'
                : 'Write a reply…  type / for templates  ·  paste images to embed inline  ·  ✨ Draft for AI'
            }
            ariaLabel="Reply body"
          />
          {aiBarOpen ? (
            <AiAssistBar assist={assist} hasBodyText={!isEmpty} disabled={false} />
          ) : null}
          {/* Formatting toolbar — Gmail-style B/I/U + lists + link + emoji.
              Sits BELOW the editor (position="bottom") so it stays close
              to the user's caret as the reply grows long. mousedown.preventDefault
              inside the toolbar keeps the editor's selection stable when
              buttons are clicked.

              `actionsAfter` folds the composer's action buttons (AI
              Draft, Polish, Templates, Attach, Save-as-template, Send)
              into the same floating sticky bar. When the bar is stuck
              at viewport bottom it morphs into a centred pill carrying
              EVERYTHING the agent needs while typing — no more reach to
              a separate row that lived below the body. */}
          <RichBodyToolbar
            editorRef={editorRef}
            onCommand={syncBodyFromEditor}
            position="bottom"
            actionsAfter={
              <>
                <ShortcutTooltip label="AI Draft" keys={['⌘', '⇧', 'D']}>
                  <ToolbarButton
                    icon={Sparkles}
                    label={drafting ? 'Drafting…' : 'Draft'}
                    title="Write a reply from the conversation, internal notes and KB. Anything you have already typed is used as your notes.  ⌘⇧D"
                    onClick={() => assist.draft()}
                    disabled={anyBusy}
                    busy={drafting}
                  />
                </ShortcutTooltip>
                <ShortcutTooltip label="Polish reply" keys={['⌘', '⇧', 'P']}>
                  <ToolbarButton
                    icon={Wand2}
                    label={polishing ? 'Polishing…' : 'Polish'}
                    title="Polish what you've already written. Fixes grammar, tone, clarity. Preserves your meaning.  ⌘⇧P"
                    onClick={() => assist.polish()}
                    disabled={anyBusy || isEmpty}
                    busy={polishing}
                  />
                </ShortcutTooltip>
                <ToolbarButton
                  icon={SlidersHorizontal}
                  label={aiBarOpen ? 'Hide AI options' : 'Guide AI'}
                  title="Tell the AI what to say, restyle your text (shorter, friendlier, firmer...), or use your saved prompts."
                  onClick={toggleAiBar}
                  disabled={false}
                  busy={false}
                />
                <div className="relative" data-insert-template-trigger>
                  <ToolbarButton
                    icon={FileText}
                    label="Templates"
                    title="Insert a saved reply template at the cursor position."
                    onClick={() => {
                      setInsertTemplateQuery('');
                      setInsertTemplateOpen((v) => !v);
                    }}
                    disabled={anyBusy}
                    busy={false}
                  />
                  {insertTemplateOpen ? (
                    <div
                      ref={insertTemplatePopoverRef}
                      className="absolute bottom-full left-0 z-50 mb-2 w-80 max-w-[90vw] overflow-hidden rounded-lg border border-border bg-background shadow-xl"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="border-b border-border/60 px-2.5 py-1.5">
                        <input
                          autoFocus
                          type="text"
                          value={insertTemplateQuery}
                          onChange={(e) => setInsertTemplateQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setInsertTemplateOpen(false);
                            }
                          }}
                          placeholder="Filter templates…"
                          className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                      {(() => {
                        const q = insertTemplateQuery.trim().toLowerCase();
                        const filtered = q.length === 0
                          ? templates
                          : templates.filter((t) => t.name.toLowerCase().includes(q));
                        if (filtered.length === 0) {
                          return (
                            <div className="px-3 py-3 text-xs text-muted-foreground">
                              {templates.length === 0 ? (
                                <>No templates yet. <a className="underline" href="/templates">Add one</a>.</>
                              ) : (
                                'No templates match.'
                              )}
                            </div>
                          );
                        }
                        return (
                          <ul className="max-h-64 overflow-y-auto py-1">
                            {filtered.map((t) => (
                              <li key={t.id}>
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    insertTemplateAtCaret(t);
                                    setInsertTemplateOpen(false);
                                  }}
                                  className="flex w-full cursor-pointer flex-col items-start gap-0.5 px-2.5 py-1.5 text-left text-xs hover:bg-accent/60"
                                >
                                  <span className="flex w-full items-center gap-1.5">
                                    <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                                    {t.visibility === 'private' ? (
                                      <span className="shrink-0 rounded bg-muted/70 px-1 py-px text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                                        Private
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="line-clamp-1 text-2xs text-muted-foreground">
                                    {htmlToPlain(t.body).replace(/\s+/g, ' ').slice(0, 100)}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        );
                      })()}
                      <div className="border-t border-border/60 px-2.5 py-1 text-3xs text-muted-foreground">
                        Inserts at the cursor · or type <kbd className="rounded border border-border/60 bg-muted px-1 font-mono">/</kbd> on a fresh line
                      </div>
                    </div>
                  ) : null}
                </div>
                <ToolbarButton
                  icon={Paperclip}
                  label="Attach"
                  title={`Attach files (≤25MB each, max ${attachments.maxFiles}). You can also paste or drag images directly.`}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={anyBusy || attachments.items.length >= attachments.maxFiles}
                  busy={false}
                />
                <ToolbarButton
                  icon={BookmarkPlus}
                  label="Save"
                  title="Save this reply as a template for re-use later. Choose shared (whole team) or private (just you)."
                  onClick={() => setSaveTemplateOpen(true)}
                  disabled={anyBusy || isEmpty}
                  busy={false}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) attachments.add(e.target.files);
                    e.target.value = '';
                  }}
                />
                {/* Send is the primary action — promoted to a full
                    coloured Button so it visually stands apart from
                    the icon siblings. `ml-auto` pushes it to the FAR
                    RIGHT of the toolbar (or the far right of whatever
                    row it wraps to on narrow mobile screens) — same
                    affordance position as Gmail / Slack / every email
                    client built since 2010. */}
                <span className="ml-auto inline-flex items-center gap-2">
                  {afterSend ? (
                    <span
                      className="inline-flex max-w-[15rem] items-center gap-1 rounded-full bg-tint-info px-2 py-0.5 text-2xs font-medium text-tint-info-fg"
                      title="Runs once this reply has been sent. Undo cancels it too."
                      data-testid="after-send-chip"
                    >
                      <Zap className="h-3 w-3 shrink-0" aria-hidden />
                      <span className="truncate">Then: {describeAfterSend(afterSend, memberName)}</span>
                      <button
                        type="button"
                        aria-label="Remove the after-sending action"
                        onClick={() => setAfterSend(null)}
                        className="-mr-0.5 inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-black/10"
                      >
                        <X className="h-3 w-3" aria-hidden />
                      </button>
                    </span>
                  ) : null}
                  <ShortcutTooltip
                    label={mode === 'forward' ? 'Forward' : 'Send reply'}
                    keys={['⌘', '↵']}
                  >
                    <Button
                      type="button"
                      size="sm"
                      disabled={isEmpty || to.length === 0}
                      onClick={() => send()}
                      // Fixed min-width + centred content so the toolbar (especially
                      // the mobile island) doesn't reflow when the label changes
                      // between Send and Forward. The button never shows a spinner:
                      // a click only queues the message, and the composer clears.
                      className="h-7 min-w-[6rem] justify-center rounded-md px-2.5 text-xs font-medium"
                    >
                      <Send className="mr-1 h-3.5 w-3.5" aria-hidden />
                      {mode === 'forward' ? 'Forward' : 'Send'}
                    </Button>
                  </ShortcutTooltip>
                </span>
              </>
            }
          />
          {attachments.items.some((it) => it.previewUrl === null) ? (
            <div className="border-t border-border/40 px-3 py-2">
              <AttachmentsRow items={attachments.items} onRemove={attachments.remove} />
            </div>
          ) : null}
        </div>


        {/* Slash-menu */}
        {menuOpen ? (
          <div className="absolute bottom-full left-0 z-50 mb-2 w-80 max-w-[90vw] overflow-hidden rounded-lg border border-border bg-background shadow-xl">
            <div className="border-b border-border/60 px-2.5 py-1.5 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              Templates {menuQuery ? `· "${menuQuery}"` : ''}
            </div>
            {matches.length === 0 ? (
              <div className="px-3 py-3 text-xs text-muted-foreground">
                No templates match. {templates.length === 0 ? <>Add one in <a className="underline" href="/templates">Templates</a>.</> : null}
              </div>
            ) : (
              <ul className="max-h-64 overflow-y-auto py-1">
                {matches.map((t, i) => {
                  const active = i === menuActiveIdx;
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        onClick={() => selectTemplate(t)}
                        onMouseEnter={() => setMenuActiveIdx(i)}
                        className={cn(
                          'flex w-full cursor-pointer flex-col items-start gap-0.5 px-2.5 py-1.5 text-left text-xs',
                          active ? 'bg-accent' : 'hover:bg-accent/60',
                        )}
                      >
                        <span className="flex w-full items-center gap-1.5">
                          <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                          {t.visibility === 'private' ? (
                            <span className="shrink-0 rounded bg-muted/70 px-1 py-px text-3xs font-medium uppercase tracking-wide text-muted-foreground">
                              Private
                            </span>
                          ) : null}
                        </span>
                        <span className="line-clamp-1 text-2xs text-muted-foreground">
                          {/* Decode HTML → plain so the slash menu doesn't
                              show <p> tags in the preview line. */}
                          {htmlToPlain(t.body).replace(/\s+/g, ' ').slice(0, 100)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="border-t border-border/60 px-2.5 py-1 text-3xs text-muted-foreground">
              ↑↓ navigate · ↵ insert · esc close
            </div>
          </div>
        ) : null}
      </div>

      <p className="mt-1.5 px-1 text-3xs text-muted-foreground">
        Sent via Gmail ·{' '}
        {hasOwnSignature ? (
          <>
            your signature is appended ·{' '}
            <Link to="/settings" className="underline-offset-2 hover:underline">
              edit
            </Link>
          </>
        ) : (
          <>
            using the team default signature ·{' '}
            <Link to="/settings" className="underline-offset-2 hover:underline">
              set yours
            </Link>
          </>
        )}
        {draftWasRestored ? (
          <>
            {' '}·{' '}
            <button
              type="button"
              onClick={() => {
                setBody('');
                editorRef.current?.setHtml('');
                resetRecipients(defaultMode);
                setShowCc(false);
                setShowBcc(false);
                setSubject('');
                clearDraft(ticketId);
                setDraftWasRestored(false);
                toast.success('Draft discarded');
              }}
              className="cursor-pointer underline-offset-2 hover:text-tint-critical-fg hover:underline"
            >
              discard draft
            </button>
          </>
        ) : null}
      </p>
      {/* Save-as-template dialog. Reads the live `body` HTML on submit
          via the prop — no snapshot needed; the dialog renders nothing
          when closed so there's no perf concern. */}
      <SaveAsTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        bodyHtml={body}
        // After Save-as-template uploads any pasted blob: images to
        // permanent storage, mirror the rewritten HTML back into the
        // live editor + body state. Without this, the editor still
        // holds the blob: src and a subsequent Send finds nothing to
        // re-upload (the pending-images registry was just drained by
        // the template save), so the recipient gets a broken image.
        onBodyHtmlRewritten={(rewrittenHtml) => {
          setBody(rewrittenHtml);
          editorRef.current?.setHtml(rewrittenHtml);
        }}
      />
    </div>
  );
}

function ModePill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-6 cursor-pointer rounded-full px-2.5 text-2xs font-medium transition-colors',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function ToolbarButton({
  icon: Icon,
  label,
  title,
  onClick,
  disabled,
  busy,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  title: string;
  onClick: () => void;
  disabled: boolean;
  busy: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        // Mobile: square-ish icon-only button (h-7 w-7 — matches the
        // adjacent formatting glyphs from RichBodyToolbar so the
        // floating bar reads as one visually consistent row). The
        // label is hidden because labeled action buttons + the Send
        // cluster otherwise exceed the 375px viewport. The `title`
        // prop still surfaces the label as a long-press tooltip on
        // touch + hover tooltip on desktop.
        // sm:+ : full label restored, padding widens, gap re-added.
        'inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center gap-1 rounded text-xs font-medium transition-colors',
        'sm:h-7 sm:w-auto sm:rounded-md sm:px-2',
        'text-muted-foreground hover:bg-accent hover:text-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
        busy && 'animate-pulse',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
